import { Fragment, useState } from "react";
import { useQuery } from "react-query";
import { Button, Upload, message, notification, Spin, Result, Tooltip } from "antd";
import { UploadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { BASE_PATH, API_VERSION } from "../../api/api";
import { ACCESS_TOKEN } from "../../api/auth";
import { Searcher, TableAntd } from "../../components/commons";
import { createColumns } from "./formatTable";
import { useAuth } from "../../utils/useAuth";
import { uploadProps } from "../../utils/functions";
import { DATE_LIKE_NAME } from "../../utils/functions";
import {
  getAll,
  downloadExcelPlantillaBDParticipantes,
  downloadExcelFileBDCredenciales
} from "../../api/credencial";

export default function Credenciales() {
  const { data, isError, error, isLoading } = useQuery("bdposibles-participantes", getAll);
  const [isUploading, setIsUploading] = useState(false);
  const [filterTable, setFilterTable] = useState(null);
  const [page, setPage] = useState(1);
  const { isAdmin } = useAuth();

  //  --------------- BDPosibleParticipante ---------------
  const uploadCredencialParticipante = {
    name: "credencial",
    action: `${BASE_PATH}/${API_VERSION}/credenciales/upload`,
    headers: { authorization: ACCESS_TOKEN },
    onChange(info) {
      if (info.file.status !== "uploading") {
        setIsUploading(true);
      }
      if (info.file.status === "done") {
        setIsUploading(false);
        window.location.reload();
        message.success(`${info.file.name} subido correctamente`);
      } else if (info.file.status === "error") {
        notification.error({
          message: "Operación fallida",
          description: info.file.response.message
        });
        setIsUploading(false);
      }
    }
  };

  // ------ Search filter ------
  /**
   * search
   * @param {String} value
   * Busca por cualquier valor de la columna
   * fuentes:
   * https://codesandbox.io/s/basic-usage-ant-design-demo-2swep?file=/index.js
   * https://github.com/ant-design/ant-design/issues/22969#issuecomment-611738230
   */
  const search = (value) => {
    const tempFilterTable = data.payload.filter((o) =>
      Object.keys(o).some((k) => String(o[k]).toLowerCase().includes(value.toLowerCase()))
    );

    setFilterTable(tempFilterTable);
  };

  // ------ Show modal edit record -----------
  const showEditModal = (record) => {
    console.log(record);
    alert("Descarga credencial");
  };

  // if (!isAdmin) return <Redirect to="/app" />;

  if (isLoading) {
    return <Spin tip="Cargando" className="w-full py-60" />;
  }

  if (isError) {
    return (
      <Result
        status="500"
        title="Algo salió mal"
        subTitle={error.message}
        extra={
          <Button onClick={() => window.location.reload()} type="primary">
            Recargar
          </Button>
        }
      />
    );
  }

  // ------------- Descarga plantilla -------------
  const downloadPlantillaBDCredencialExel = async () => {
    const blob = await downloadExcelPlantillaBDParticipantes();

    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `par_participante_credencial-${DATE_LIKE_NAME}.xlsx`);
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  // ------------- Descarga data excel -------------
  const downloadBDCrecencialesExcel = async () => {
    const blob = await downloadExcelFileBDCredenciales();

    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `par_participante_credencial-${DATE_LIKE_NAME}.xlsx`);
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  // ------ Formato para que se muestre en la tabla ------
  const columnsToTable = createColumns(page, showEditModal);

  return (
    <Fragment>
      <div className="my-4 gap-4 mr-4 flex flex-col flex-wrap  md:flex-row justify-between md:flex-nowrap">
        <span className="font-bold text-lg text-blue-500">
          ({data.payload.length || 0}) CREDENCIALES PARTICIPANTES
        </span>
        <div className="flex gap-4 flex-col flex-wrap  md:flex-row md:flex-nowrap">
          {isAdmin && (
            <Button
              onClick={downloadBDCrecencialesExcel}
              icon={<FileExcelOutlined />}
              className="focus:outline-none text-white text-sm h-9 px-5 rounded-md bg-green-500 hover:bg-green-600 hover:shadow-lg"
            >
              Descarga Excel
            </Button>
          )}

          {isAdmin && (
            <Button
              onClick={downloadPlantillaBDCredencialExel}
              icon={<FileExcelOutlined />}
              className="focus:outline-none text-white text-sm h-9 px-5 rounded-md bg-green-500 hover:bg-green-600 hover:shadow-lg"
            >
              Descargar plantilla
            </Button>
          )}

          {isAdmin && (
            <Upload showUploadList={false} {...uploadCredencialParticipante} accept=".xlsx">
              <Button
                loading={isUploading}
                icon={<UploadOutlined />}
                className="text-white rounded-md text-sm h-9 px-5 bg-yellow-500 hover:bg-yellow-600 hover:shadow-lg"
              >
                Subir Excel
              </Button>
            </Upload>
          )}

          <Tooltip title="Todas las fotos deben estar en un archivo formato zip sin importar el nombre del archivo, cada archivo debe ser el numero de DNI con la extensión -FOT.jpg ejemplo 00418322-FOT.jpg">
            <Upload
              {...uploadProps({
                name: "mas_archivo",
                action: "/sistema/upload-zip-foto-rostro",
                maxSize: 2000 // 2gb
              })}
              multiple
            >
              <Button
                icon={<UploadOutlined />}
                className="text-white rounded-md text-sm h-9 px-5 bg-blue-400 hover:bg-blue-400 hover:shadow-lg"
              >
                Subir Fotos
              </Button>
            </Upload>
          </Tooltip>
        </div>
      </div>

      <Searcher onSearch={search} placeholder="Busca algún posible participante" />

      <TableAntd
        columns={columnsToTable}
        dataSource={filterTable == null ? data.payload : filterTable}
        setPage={setPage}
        pageSize={50}
      />
    </Fragment>
  );
}
